import React, { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { t } from '@lingui/macro'

import IStudyData from '../types/IStudyData'
import StudyDataService from '../DataServices/StudyDataService'
import Checkbox from '../utils/controls/Checkbox'

type StudyConfigDialogProps = {
  onClose: () => void
  open: boolean
  setStudy: React.Dispatch<React.SetStateAction<IStudyData | null>>
  study: IStudyData
}

function StudyConfigDialog({
  onClose,
  open,
  setStudy,
  study,
}: StudyConfigDialogProps): JSX.Element {
  const [isHelper, setIsHelper] = useState<boolean>(study.isHelper)
  const [isTemplate, setIsTemplate] = useState<boolean>(study.isTemplate)

  const handleSaveDialog = (): void => {
    if (isHelper !== study.isHelper || isTemplate !== study.isTemplate) {
      const studyToUpdate: IStudyData = { ...study, ...{ isHelper, isTemplate } }
      StudyDataService.updateOrCreate(study.id, studyToUpdate)
        .then(() => {
          console.log('Study config updated', studyToUpdate.id)
          setStudy({ ...studyToUpdate })
          onClose()
        })
        .catch((err) => {
          console.log('ERROR: An error occurred while study config updating', err, err.response)
        })
    } else {
      onClose()
    }
  }

  const handleCloseDialog = (): void => {
    setIsHelper(study.isHelper)
    setIsTemplate(study.isTemplate)
    onClose()
  }

  return (
    <Dialog
      aria-describedby="alert-dialog-description"
      aria-labelledby="alert-dialog-title"
      onClose={handleCloseDialog}
      open={open}
    >
      <DialogTitle id="alert-dialog-title" sx={{ p: 2 }}>
        <Box display="flex">
          <Box flexGrow={1}>{t`Configure study`}</Box>
          <Tooltip title={t`Close`}>
            <IconButton onClick={onClose} sx={{ color: 'text.secondary', mr: 0, p: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 2 }}>
        <DialogContentText id="alert-dialog-description" sx={{ fontSize: '0.95rem', pb: 3 }}>
          {t`Here are the additional options available for this study.`}
        </DialogContentText>
        <Box>
          <Checkbox
            checked={isHelper}
            color="primary"
            label={
              <Typography sx={{ fontSize: '0.95rem' }}>{t`This study is a helper`}</Typography>
            }
            labelPlacement="end"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
              setIsHelper(checked)
            }
            sx={{ py: 0 }}
          />
        </Box>
        <Box>
          <Checkbox
            checked={isTemplate}
            color="primary"
            label={
              <Typography sx={{ fontSize: '0.95rem' }}>{t`This study is a template`}</Typography>
            }
            labelPlacement="end"
            onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) =>
              setIsTemplate(checked)
            }
            sx={{ py: 0 }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseDialog} size="small" variant="outlined">{t`Cancel`}</Button>
        <Button
          autoFocus
          onClick={handleSaveDialog}
          size="small"
          variant="contained"
        >{t`Save`}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(StudyConfigDialog)
